import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Switch, Select, message, Space } from 'antd';
import { UserOutlined, SaveOutlined } from '@ant-design/icons';
import { useUser } from '../../contexts/UserContext';
import { UserService, UserUpdate } from '../../services/api/userService';

const { Option } = Select;
const { TextArea } = Input;

interface UserProfileDialogProps {
  visible: boolean;
  onClose: () => void;
}

export const UserProfileDialog: React.FC<UserProfileDialogProps> = ({
  visible,
  onClose
}) => {
  const { user, updateUser, refreshUser } = useUser();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible && user) {
      form.setFieldsValue({
        username: user.username,
        email: user.email,
        full_name: user.fullName,
        is_active: user.isActive,
        default_ui_role: user.preferences?.defaultUIRole || 'architect',
        default_environment: user.preferences?.defaultEnvironment || 'Company',
        theme: user.preferences?.theme || 'light',
        notifications_enabled: user.preferences?.notifications ?? true,
      });
    }
  }, [visible, user, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      if (!user) {
        message.error('User not found');
        return;
      }

      const updateData: UserUpdate = {
        username: values.username,
        email: values.email,
        full_name: values.full_name,
        is_active: values.is_active,
      };

      await UserService.update(user.id, updateData);

      // Update preferences if they exist
      if (user.preferences) {
        await updateUser({
          preferences: {
            defaultUIRole: values.default_ui_role,
            defaultEnvironment: values.default_environment,
            theme: values.theme,
            notifications: values.notifications_enabled,
          }
        });
      }

      await refreshUser();
      message.success('Profile updated successfully');
      onClose();
    } catch (error: any) {
      console.error('Error updating profile:', error);
      message.error(error.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={
        <Space>
          <UserOutlined />
          <span>User Profile</span>
        </Space>
      }
      open={visible}
      onOk={handleSave}
      onCancel={handleCancel}
      confirmLoading={loading}
      width={600}
      okText="Save"
      okButtonProps={{ icon: <SaveOutlined /> }}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          is_active: true,
          default_ui_role: 'architect',
          default_environment: 'Company',
          theme: 'light',
          notifications_enabled: true,
        }}
      >
        <Form.Item
          label="Username"
          name="username"
          rules={[{ required: true, message: 'Please enter username' }]}
        >
          <Input placeholder="Enter username" autoComplete="username" />
        </Form.Item>

        <Form.Item
          label="Email"
          name="email"
          rules={[
            { required: true, message: 'Please enter email' },
            { type: 'email', message: 'Please enter a valid email' }
          ]}
        >
          <Input type="email" placeholder="Enter email address" />
        </Form.Item>

        <Form.Item
          label="Full Name"
          name="full_name"
        >
          <Input placeholder="Enter full name" />
        </Form.Item>

        <Form.Item
          label="Status"
          name="is_active"
          valuePropName="checked"
        >
          <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
        </Form.Item>

        <Form.Item
          label="Default UI Role"
          name="default_ui_role"
        >
          <Select placeholder="Select default UI role">
            <Option value="architect">Architect</Option>
            <Option value="operator">Operator</Option>
            <Option value="coach">Coach</Option>
            <Option value="integrator">Integrator</Option>
            <Option value="development">Development</Option>
            <Option value="marketfit">MarketFit</Option>
          </Select>
        </Form.Item>

        <Form.Item
          label="Default Environment"
          name="default_environment"
        >
          <Select placeholder="Select default environment">
            <Option value="Prime">Prime</Option>
            <Option value="Company">Company</Option>
            <Option value="Sandbox">Sandbox</Option>
          </Select>
        </Form.Item>

        <Form.Item
          label="Theme"
          name="theme"
        >
          <Select placeholder="Select theme">
            <Option value="light">Light</Option>
            <Option value="dark">Dark</Option>
          </Select>
        </Form.Item>

        <Form.Item
          label="Notifications"
          name="notifications_enabled"
          valuePropName="checked"
        >
          <Switch checkedChildren="Enabled" unCheckedChildren="Disabled" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

